import Icon from '@mdi/react';
import { useLocation } from '@reach/router';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mdiDotsHorizontal } from 'src/assets/icons/mdiIcons';
import { Link, linkStyles } from 'src/components/Link';
import {
  type IBreadcrumbItem,
  MetatagProps,
  getBreadcrumbsFromMeta,
} from 'src/utils/metatagHelpers';
import { useBreadcrumb } from './useBreadcrumb';

interface BreadcrumbRootProps {
  children: React.ReactNode;
  'aria-label'?: string;
}

export const BreadcrumbRoot: React.FC<BreadcrumbRootProps> = ({ children, ...props }) => {
  const { t } = useTranslation();
  const ariaLabel = props['aria-label'] || (t('Breadcrumbs') as string);
  return (
    <nav aria-label={ariaLabel} className="pds-p-4 md:pds-px-4.5 lg:pds-px-5">
      {children}
    </nav>
  );
};

interface BreadcrumbListProps {
  children: React.ReactNode;
}

export const BreadcrumbList = React.forwardRef<HTMLOListElement, BreadcrumbListProps>(
  ({ children }, ref) => (
    <ol
      ref={ref}
      className="pds-m-0 pds-flex pds-flex-wrap pds-items-center pds-p-0 pds-text-body-sm"
    >
      {children}
    </ol>
  ),
);

export const BreadcrumbSeparator = React.forwardRef<
  HTMLSpanElement,
  React.HTMLProps<HTMLSpanElement>
>(({ style, ...props }, ref) => (
  <span
    ref={ref}
    className="pds-flex pds-items-center pds-px-3 pds-text-surface-text"
    aria-hidden="true"
    style={style}
    {...props}
  >
    <span aria-hidden="true">/</span>
  </span>
));

interface BreadcrumbItemProps extends React.HTMLProps<HTMLLIElement> {
  item: IBreadcrumbItem;
  onClick: () => void;
}

export const BreadcrumbItem = React.forwardRef<HTMLAnchorElement, BreadcrumbItemProps>(
  ({ item, onClick, style, ...props }, ref) => {
    const location = useLocation();
    if (!item) return null;

    const isCurrent = item['@id'] === '' || item['@id'] === location.pathname;

    return (
      <li
        aria-current={isCurrent ? 'page' : undefined}
        className="pds-flex pds-items-center pds-text-surface-text"
        style={style}
        {...props}
      >
        {isCurrent ? (
          <span className="pds-font-light pds-text-surface-text">{item.name}</span>
        ) : (
          <Link
            onClick={onClick}
            partiallyActive
            ref={ref}
            className="pds-no-underline"
            to={item['@id']}
          >
            {item.name}
          </Link>
        )}
      </li>
    );
  },
);

interface BreadcrumbEllipsisProps {
  onClick: () => void;
}

export const BreadcrumbEllipsis: React.FC<BreadcrumbEllipsisProps> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <li className="pds-flex pds-items-center">
      <button
        aria-label={t('Show More Breadcrumbs') as string}
        type="button"
        className={linkStyles({
          class: 'pds-group pds-rounded-circle pds-bg-transparent',
        })}
        onClick={onClick}
      >
        <Icon path={mdiDotsHorizontal} className="!pds-m-0" size={1} aria-hidden="true" />
      </button>
    </li>
  );
};

interface BreadcrumbProps {
  metaData?: Array<MetatagProps>;
}

export const Breadcrumbs: React.FC<BreadcrumbProps> = ({ metaData }) => {
  const items = useMemo(() => {
    return metaData ? getBreadcrumbsFromMeta(metaData) ?? [] : [];
  }, [metaData]);

  const {
    shouldRender,
    shouldRenderEllipsis,
    setShowBreadcrumb,
    showBreadcrumbs,
    visibleItemsStart,
    hiddenItems,
    visibleItemEnd,
  } = useBreadcrumb(items);

  if (!items.length || !shouldRender) return null;

  const handleGA = (name: string) =>
    sendIt({
      event: 'nav_click',
      eventParams: {
        nav_item: name,
        nav_type: 'breadcrumb',
      },
    });

  return (
    <BreadcrumbRoot>
      <BreadcrumbList>
        {visibleItemsStart.map((item, idx) => (
          <React.Fragment key={item['@id']}>
            <BreadcrumbItem item={item} onClick={() => handleGA(item.name)} />
            {(shouldRenderEllipsis || idx < visibleItemsStart.length - 1) && (
              <BreadcrumbSeparator />
            )}
          </React.Fragment>
        ))}

        {shouldRenderEllipsis && !showBreadcrumbs && (
          <BreadcrumbEllipsis onClick={() => setShowBreadcrumb(true)} />
        )}

        {shouldRenderEllipsis &&
          hiddenItems.map((item, idx) => (
            <React.Fragment key={item['@id']}>
              {idx > 0 && (
                <BreadcrumbSeparator style={{ display: showBreadcrumbs ? undefined : 'none' }} />
              )}
              <BreadcrumbItem
                item={item}
                onClick={() => handleGA(item.name)}
                style={{ display: showBreadcrumbs ? undefined : 'none' }}
              />
            </React.Fragment>
          ))}

        {shouldRenderEllipsis && visibleItemEnd && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem item={visibleItemEnd} onClick={() => handleGA(visibleItemEnd.name)} />
          </>
        )}
      </BreadcrumbList>
    </BreadcrumbRoot>
  );
};
